import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { MapPin, Calendar, PlayCircle, CheckCircle2, Clock, Wrench, AlertCircle, AlertTriangle, Sparkles, Camera } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useState, useRef } from "react";

type MaintenanceJob = {
  id: number;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  scheduledDate: string | null;
  address: string | null;
  apartmentName?: string | null;
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  pending:          { label: "Pending",     variant: "secondary" },
  pending_approval: { label: "Needs Approval", variant: "outline" },
  approved:         { label: "Approved",    variant: "outline" },
  assigned:         { label: "Assigned",    variant: "outline" },
  in_progress:      { label: "In Progress", variant: "default" },
  completed:        { label: "Completed",   variant: "secondary" },
  cancelled:        { label: "Cancelled",   variant: "destructive" },
};

const priorityColors: Record<string, string> = {
  urgent: "text-red-600",
  high:   "text-orange-500",
  medium: "text-yellow-600",
  low:    "text-blue-500",
};

type MaintenancePhoto = { id: number; url: string; phase: string | null; caption: string | null };

function PhotoSection({ jobId, jobStatus }: { jobId: number; jobStatus: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data: photos = [] } = useQuery<MaintenancePhoto[]>({
    queryKey: ["/api/maintenance/jobs", jobId, "photos"],
    queryFn: async () => {
      const res = await fetch(`/api/maintenance/jobs/${jobId}/photos`);
      if (!res.ok) throw new Error("Failed to load photos");
      return res.json();
    },
  });

  const beforePhotos = photos.filter(p => p.phase === "before" || !p.phase);
  const afterPhotos  = photos.filter(p => p.phase === "after");
  const canUpload = jobStatus !== "cancelled";
  const uploadPhase = (jobStatus === "pending" || jobStatus === "pending_approval" || jobStatus === "approved" || jobStatus === "assigned") ? "before" : "after";

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("phase", uploadPhase);
      const res = await fetch(`/api/maintenance/jobs/${jobId}/photos`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error("Upload failed");
      qc.invalidateQueries({ queryKey: ["/api/maintenance/jobs", jobId, "photos"] });
      toast({ title: "Photo uploaded" });
    } catch {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const renderGrid = (list: MaintenancePhoto[]) => (
    <div className="flex gap-1.5 flex-wrap">
      {list.map(p => (
        <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer">
          <img src={p.url} alt={p.caption ?? ""} className="w-16 h-16 object-cover rounded border hover:opacity-80 transition-opacity" />
        </a>
      ))}
    </div>
  );

  return (
    <div className="border-t pt-3 space-y-2">
      {beforePhotos.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-muted-foreground mb-1">Before</p>
          {renderGrid(beforePhotos)}
        </div>
      )}
      {afterPhotos.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-muted-foreground mb-1">After</p>
          {renderGrid(afterPhotos)}
        </div>
      )}
      {photos.length === 0 && <p className="text-xs text-muted-foreground">No photos yet.</p>}
      {canUpload && (
        <>
          <input ref={inputRef} type="file" accept="image/*,video/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
          <Button size="sm" variant="default" className="w-full" onClick={() => inputRef.current?.click()} disabled={uploading}>
            <Camera className="w-3.5 h-3.5 mr-1.5" />{uploading ? "Uploading…" : uploadPhase === "before" ? "Add before photo" : "Add after photo"}
          </Button>
        </>
      )}
    </div>
  );
}

function JobCard({ job, onUpdate }: {
  job: MaintenanceJob;
  onUpdate: (id: number, status: string, description?: string) => void;
}) {
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const cfg = statusConfig[job.status] ?? { label: job.status, variant: "outline" as const };
  const pColor = priorityColors[job.priority] ?? "text-muted-foreground";

  const handleComplete = () => {
    onUpdate(job.id, "completed", note || undefined);
    setShowNote(false);
    setNote("");
  };

  return (
    <Card className="border border-border/50">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-sm leading-tight">{job.title}</h3>
          <Badge variant={cfg.variant} className="shrink-0">{cfg.label}</Badge>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-medium uppercase ${pColor}`}>
            <AlertCircle className="w-3 h-3 inline mr-1" />
            {job.priority}
          </span>
        </div>

        <div className="space-y-1 text-sm text-muted-foreground">
          {(job.apartmentName ?? job.address) && (
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 shrink-0 text-blue-500" />
              {job.address
                ? <a href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`} target="_blank" rel="noopener noreferrer" className="truncate text-blue-600 underline underline-offset-2 hover:text-blue-800">{job.apartmentName ?? job.address}</a>
                : <span className="truncate">{job.apartmentName}</span>}
            </div>
          )}
          {job.scheduledDate && (
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              <span>{format(new Date(job.scheduledDate.includes("T") ? job.scheduledDate : job.scheduledDate + "T12:00:00"), "MMM d, yyyy")}</span>
            </div>
          )}
        </div>

        {job.description && (
          <p className="text-sm text-muted-foreground border-t pt-2 line-clamp-3">{job.description}</p>
        )}

        {showNote && (
          <Textarea
            placeholder="Add a note (optional)…"
            value={note}
            onChange={e => setNote(e.target.value)}
            className="text-sm"
            rows={2}
          />
        )}

        {job.status !== "completed" && job.status !== "cancelled" && (
          <div className="flex gap-2 pt-1">
            {(job.status === "pending" || job.status === "approved" || job.status === "assigned") && (
              <Button size="sm" className="flex-1" onClick={() => onUpdate(job.id, "in_progress")}>
                <PlayCircle className="w-4 h-4 mr-1.5" /> Start Job
              </Button>
            )}
            {job.status === "in_progress" && (
              <>
                <Button size="sm" variant="ghost" className="shrink-0" onClick={() => setShowNote(v => !v)}>
                  {showNote ? "Hide note" : "Add note"}
                </Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={handleComplete}>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete
                </Button>
              </>
            )}
          </div>
        )}
        {job.status === "completed" && (
          <div className="flex items-center gap-1.5 text-xs text-green-600 pt-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Completed
          </div>
        )}
        <PhotoSection jobId={job.id} jobStatus={job.status} />
      </CardContent>
    </Card>
  );
}

export default function WorkerMaintenanceTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<"pending" | "active" | "completed">("pending");

  const { data: jobs = [], isLoading } = useQuery<MaintenanceJob[]>({
    queryKey: ["/api/maintenance/jobs"],
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, description }: { id: number; status: string; description?: string }) => {
      const res = await apiRequest("PATCH", `/api/maintenance/jobs/${id}`, { status, description });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/maintenance/jobs"] });
      toast({ title: "Job updated" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const now = new Date();
  const todayStr = now.toDateString();
  const tomorrowStr = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toDateString();

  const getJobDate = (j: MaintenanceJob) => {
    if (!j.scheduledDate) return null;
    const s = j.scheduledDate;
    return new Date(s.includes("T") ? s : s + "T12:00:00");
  };
  const sortByTime = (arr: MaintenanceJob[]) =>
    [...arr].sort((a, b) => (getJobDate(a)?.getTime() ?? Infinity) - (getJobDate(b)?.getTime() ?? Infinity));

  const pendingJobs  = jobs.filter(j => j.status === "pending" || j.status === "approved" || j.status === "assigned" || j.status === "pending_approval");
  const inProgress   = jobs.filter(j => j.status === "in_progress");
  const done         = jobs.filter(j => j.status === "completed" || j.status === "cancelled");
  const viewJobs     = filter === "pending" ? pendingJobs : filter === "active" ? inProgress : done;
  const overdueJobs  = sortByTime(viewJobs.filter(j => { const d = getJobDate(j); return d && d < now && d.toDateString() !== todayStr; }));
  const todayJobs    = sortByTime(viewJobs.filter(j => { const d = getJobDate(j); return d && d.toDateString() === todayStr; }));
  const tomorrowJobs = sortByTime(viewJobs.filter(j => { const d = getJobDate(j); return d && d.toDateString() === tomorrowStr; }));
  const upcomingJobs = sortByTime(viewJobs.filter(j => { const d = getJobDate(j); return d && d > now && d.toDateString() !== todayStr && d.toDateString() !== tomorrowStr; }));
  const undatedJobs  = viewJobs.filter(j => !getJobDate(j));

  const renderSection = (title: string, Icon: React.ElementType, color: string, sectionJobs: MaintenanceJob[]) => {
    if (!sectionJobs.length) return null;
    return (
      <section className="space-y-2">
        <div className={`flex items-center gap-2 text-sm font-medium ${color}`}>
          <Icon className="w-4 h-4" />
          {title} ({sectionJobs.length})
        </div>
        {sectionJobs.map(j => (
          <JobCard key={j.id} job={j} onUpdate={(id, status, description) => updateMutation.mutate({ id, status, description })} />
        ))}
      </section>
    );
  };

  if (isLoading) return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-36 w-full" />)}
    </div>
  );

  if (!jobs.length) return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
      <Wrench className="w-10 h-10 opacity-30" />
      <p className="text-sm">No maintenance jobs assigned to you.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant={filter === "pending" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setFilter("pending")}>
          Pending ({pendingJobs.length})
        </Button>
        <Button variant={filter === "active" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setFilter("active")}>
          <Clock className="w-4 h-4 mr-1" /> Active ({inProgress.length})
        </Button>
        <Button variant={filter === "completed" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setFilter("completed")}>
          <CheckCircle2 className="w-4 h-4 mr-1" /> Done ({done.length})
        </Button>
      </div>

      {viewJobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
          <Wrench className="w-8 h-8 opacity-30" />
          <p className="text-sm">
            {filter === "pending" ? "No pending jobs." : filter === "active" ? "No jobs in progress." : "No completed jobs yet."}
          </p>
        </div>
      ) : (
        <>
          {renderSection("Overdue", AlertTriangle, "text-destructive", overdueJobs)}
          {renderSection("Today", Clock, "text-primary", todayJobs)}
          {renderSection("Tomorrow", Calendar, "text-blue-500", tomorrowJobs)}
          {renderSection("Upcoming", Sparkles, "text-muted-foreground", upcomingJobs)}
          {renderSection("Unscheduled", Clock, "text-muted-foreground", undatedJobs)}
        </>
      )}
    </div>
  );
}
